import React from 'react';
import { Info, AlertTriangle, Lightbulb } from 'lucide-react';
import type { ArticleSection } from '../../types/blog';
import { renderArticleText } from '../../utils/renderArticleText';

interface ArticleBodyProps {
  content: ArticleSection[];
}

const calloutConfig = {
  info: {
    Icon: Info,
    border: 'border-sky-500/40',
    bg: 'bg-sky-500/10',
    text: 'text-sky-300',
    iconColor: 'text-sky-400',
  },
  warning: {
    Icon: AlertTriangle,
    border: 'border-amber-500/40',
    bg: 'bg-amber-500/10',
    text: 'text-amber-200',
    iconColor: 'text-amber-400',
  },
  tip: {
    Icon: Lightbulb,
    border: 'border-emerald-500/40',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-200',
    iconColor: 'text-emerald-400',
  },
};

const ArticleBody: React.FC<ArticleBodyProps> = ({ content }) => {
  return (
    <div className="space-y-5">
      {content.map((section, index) => {
        switch (section.type) {
          case 'h2':
            return (
              <h2
                key={index}
                className="text-xl md:text-2xl font-bold text-fg mt-10 mb-4"
              >
                {section.text}
              </h2>
            );

          case 'h3':
            return (
              <h3
                key={index}
                className="text-lg md:text-xl font-semibold text-fg mt-7 mb-3"
              >
                {section.text}
              </h3>
            );

          case 'p':
            return (
              <p key={index} className="text-fg-muted leading-relaxed">
                {section.text ? renderArticleText(section.text) : null}
              </p>
            );

          case 'ul':
            return (
              <ul key={index} className="space-y-2 ml-4">
                {(section.items ?? []).map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-fg-muted text-sm leading-relaxed">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent-strong" />
                    <span>{renderArticleText(item)}</span>
                  </li>
                ))}
              </ul>
            );

          case 'ol':
            return (
              <ol key={index} className="space-y-2 ml-4 list-decimal list-inside">
                {(section.items ?? []).map((item, i) => (
                  <li key={i} className="text-fg-muted text-sm leading-relaxed">
                    {renderArticleText(item)}
                  </li>
                ))}
              </ol>
            );

          case 'callout': {
            const variant = section.variant ?? 'info';
            const cfg = calloutConfig[variant];
            const { Icon } = cfg;
            return (
              <div
                key={index}
                className={`flex gap-3 rounded-xl border p-4 ${cfg.border} ${cfg.bg}`}
              >
                <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${cfg.iconColor}`} />
                <div>
                  {section.title && (
                    <p className={`font-semibold mb-1 ${cfg.text}`}>{section.title}</p>
                  )}
                  {section.text && (
                    <p className={`text-sm leading-relaxed ${cfg.text}`}>
                      {renderArticleText(section.text)}
                    </p>
                  )}
                </div>
              </div>
            );
          }

          case 'image':
            if (!section.src) return null;
            return (
              <figure key={index} className="my-8 overflow-hidden rounded-2xl border border-line bg-canvas">
                <img
                  src={section.src}
                  alt={section.alt ?? 'Illustration article'}
                  loading="lazy"
                  className="h-auto w-full object-cover"
                />
                {section.caption && (
                  <figcaption className="px-4 py-3 text-xs text-fg-muted">
                    {section.caption}
                  </figcaption>
                )}
              </figure>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

export default ArticleBody;
